import { ReactNode } from 'react';
import { create } from 'zustand';

// App-wide imperative dialog system. A single <DialogHost /> in AppLayout
// subscribes to this store and renders a Modal based on its state. Callers
// don't have to thread state or context — they just `await dialog.confirm(...)`
// or `dialog.error(...)` from anywhere.

export type DialogKind = 'confirm' | 'error' | 'warning' | 'info' | 'success' | 'prompt';

export type DialogState = {
  open: boolean;
  kind: DialogKind;
  title: string;
  message: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  danger?: boolean;
  // 'prompt' kind only: the single line of text being asked for. The host
  // renders an input seeded with `value`; close() carries what was typed.
  // optional: the text may be left empty (OK and Enter then resolve '').
  input?: { value: string; placeholder?: string; label?: string; optional?: boolean };
  resolve?: (ok: boolean) => void;
  resolveText?: (text: string | null) => void;
};

type DialogStore = DialogState & {
  show: (s: Omit<DialogState, 'open'>) => void;
  close: (ok: boolean, text?: string) => void;
};

// The element focused when a dialog opened, so we can hand focus back to it on
// close — e.g. an automation error/warning fired on autosave should leave the
// user ON the field they just changed so they can fix it, not focus-less.
let focusBeforeDialog: HTMLElement | null = null;

export const useDialogStore = create<DialogStore>((set, get) => ({
  open: false,
  kind: 'info',
  title: '',
  message: '',
  confirmLabel: 'OK',
  show: (s) => {
    const a = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement | null;
    focusBeforeDialog = a && a !== document.body ? a : null;
    set({ ...s, open: true });
  },
  close: (ok, text) => {
    const { resolve, resolveText } = get();
    set({ open: false, resolve: undefined, resolveText: undefined, input: undefined });
    resolve?.(ok);
    resolveText?.(ok ? (text ?? '') : null);
    // Restore focus after the modal unmounts so it isn't stolen straight back.
    const el = focusBeforeDialog;
    focusBeforeDialog = null;
    if (el && document.contains(el)) setTimeout(() => { try { el.focus(); } catch { /* gone */ } }, 0);
  },
}));

// ── Public API ────────────────────────────────────────────────────────
// Everything is async so callers can `await` a confirm. Non-confirm
// dialogs resolve immediately on dismiss.

type ConfirmOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
};

type AlertOptions = {
  title?: string;
  message: ReactNode;
  confirmLabel?: string;
};

// A confirm whose button deletes, removes, voids, discards or revokes is a
// danger confirm unless the caller says otherwise (forms-18: eight Delete
// confirms rendered a teal button because they did not pass danger: true).
const DANGER_LABEL = /^(Delete|Remove|Void|Discard|Revoke)\b/;

export function showConfirm(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    useDialogStore.getState().show({
      kind: 'confirm',
      title: opts.title || 'Are you sure?',
      message: opts.message,
      confirmLabel: opts.confirmLabel || 'Confirm',
      cancelLabel: opts.cancelLabel || 'Cancel',
      danger: opts.danger ?? DANGER_LABEL.test(opts.confirmLabel || ''),
      resolve,
    });
  });
}

function showAlert(kind: DialogKind, opts: AlertOptions | string, fallbackTitle: string): Promise<void> {
  const o: AlertOptions = typeof opts === 'string' ? { message: opts } : opts;
  return new Promise((resolve) => {
    useDialogStore.getState().show({
      kind,
      title: o.title || fallbackTitle,
      message: o.message,
      confirmLabel: o.confirmLabel || 'OK',
      resolve: () => resolve(),
    });
  });
}

// Ask for one line of text — the shared replacement for window.prompt(),
// which no screen should use: it can't be styled, focus-managed or tested.
// Resolves to the text, or null if the user backed out.
export function showPrompt(opts: {
  title?: string; message?: ReactNode; label?: string;
  value?: string; placeholder?: string; confirmLabel?: string;
  // The text may be left empty ("Why (optional)"); it then resolves ''.
  optional?: boolean;
}): Promise<string | null> {
  return new Promise((resolveText) => {
    useDialogStore.getState().show({
      kind: 'prompt',
      title: opts.title || 'Enter a value',
      message: opts.message || '',
      confirmLabel: opts.confirmLabel || 'OK',
      cancelLabel: 'Cancel',
      input: { value: opts.value || '', placeholder: opts.placeholder, label: opts.label, optional: opts.optional },
      resolveText,
    });
  });
}

export const dialog = {
  confirm: showConfirm,
  prompt: showPrompt,
  error:   (opts: AlertOptions | string) => showAlert('error',   opts, 'Error'),
  warning: (opts: AlertOptions | string) => showAlert('warning', opts, 'Warning'),
  info:    (opts: AlertOptions | string) => showAlert('info',    opts, 'Notice'),
  success: (opts: AlertOptions | string) => showAlert('success', opts, 'Done'),
};

// Shortcut for the ubiquitous row-delete confirm in list tables. Handles
// the promise internally so the caller stays a plain (non-async) handler.
export function confirmDelete(
  label: string,
  action: () => void,
  message: ReactNode = 'This cannot be undone.',
): void {
  showConfirm({
    title: `Delete ${label}?`,
    message,
    confirmLabel: 'Delete',
    danger: true,
  }).then((ok) => { if (ok) action(); });
}
